/**
 * Google Tag Manager Integration
 * Loads GTM container after dataLayer initialization
 * Supports noscript fallback
 */

import { ANALYTICS_CONFIG } from './constants.js';

/**
 * Configuration object for GTM
 */
export const GTM_CONFIG = {
  container_id: process.env.VUE_APP_GTM_ID || 'GTM-XXXXX',
  async: true,
  enabled: process.env.NODE_ENV === 'production' ? true : process.env.VUE_APP_GTM_DEBUG === 'true'
};

/**
 * Load GTM script
 * Must be called AFTER dataLayer initialization
 * @param {string} container_id - GTM container ID
 * @returns {boolean} - True if GTM loaded successfully
 */
export function load_gtm(container_id = GTM_CONFIG.container_id) {
  // SSR-safe check
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  // Validate container ID
  if (!container_id || !container_id.match(/^GTM-/)) {
    if (ANALYTICS_CONFIG.debug) {
      console.warn('[GTM] Invalid container ID:', container_id);
    }
    return false;
  }

  // Skip GTM in development unless explicitly enabled
  if (process.env.NODE_ENV !== 'production' && !GTM_CONFIG.enabled) {
    if (ANALYTICS_CONFIG.debug) {
      console.log('[GTM] Skipping GTM load in development mode');
    }
    return false;
  }

  try {
    // Load GTM script
    load_gtm_script(container_id);

    // Load GTM noscript iframe
    load_gtm_noscript(container_id);

    if (ANALYTICS_CONFIG.debug) {
      console.log('[GTM] GTM loaded successfully:', container_id);
    }

    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[GTM] Error loading GTM:', error);
    }
    return false;
  }
}

/**
 * Load GTM script tag
 * @param {string} container_id - GTM container ID
 */
function load_gtm_script(container_id) {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${container_id}`;
  
  // Insert before first script tag or in head
  const first_script = document.getElementsByTagName('script')[0];
  if (first_script && first_script.parentNode) {
    first_script.parentNode.insertBefore(script, first_script);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Load GTM noscript iframe (fallback for users without JavaScript)
 * @param {string} container_id - GTM container ID
 */
function load_gtm_noscript(container_id) {
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${container_id}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  
  noscript.appendChild(iframe);
  document.body.appendChild(noscript);
}

/**
 * Send event to GTM
 * This is handled automatically via dataLayer
 * But can be called directly if needed
 * @param {Object} payload - Event payload
 */
export function send_event_to_gtm(payload) {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return false;
  }

  try {
    window.dataLayer.push(payload);
    
    if (ANALYTICS_CONFIG.debug) {
      console.log('[GTM] Event sent:', payload);
    }
    
    return true;
  } catch (error) {
    if (ANALYTICS_CONFIG.debug) {
      console.error('[GTM] Error sending event:', error);
    }
    return false;
  }
}

/**
 * Check if GTM is loaded and functional
 * @returns {boolean} - True if GTM is available
 */
export function is_gtm_loaded() {
  return typeof window !== 'undefined' && 
         typeof window.gtag === 'function' && 
         Array.isArray(window.dataLayer);
}

/**
 * Set GTM configuration
 * @param {Object} config - Configuration options
 */
export function set_gtm_config(config = {}) {
  if (!config || typeof config !== 'object') {
    return;
  }

  // Update GTM_CONFIG
  Object.assign(GTM_CONFIG, config);

  if (ANALYTICS_CONFIG.debug) {
    console.log('[GTM] Configuration updated:', GTM_CONFIG);
  }
}

/**
 * Get GTM status and diagnostics
 * @returns {Object} - Status information
 */
export function get_gtm_status() {
  return {
    container_id: GTM_CONFIG.container_id,
    enabled: GTM_CONFIG.enabled,
    loaded: is_gtm_loaded(),
    has_datalayer: typeof window !== 'undefined' && Array.isArray(window.dataLayer),
    gtag_available: typeof window !== 'undefined' && typeof window.gtag === 'function',
    environment: ANALYTICS_CONFIG.site.environment
  };
}
